import { EventTypes } from '@racemap/utilities/consts/events';
import { isGroupEvent } from '@racemap/utilities/functions/utils';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Immutable } from 'immer';
import type { StoreApi } from 'zustand';
import type { State } from '../reducers';
import type { CurrentEvent, EventsState } from './events_reducers';

export const createEventGetters = (
  get: StoreApi<State>['getState'],
): EventsState['events']['getter'] => ({
  currentEvent: () => {
    const s = get();
    const eventId = s.events.currentEventId;
    if (eventId == null) return null;
    return (s.events.items.get(eventId) as CurrentEvent) || null;
  },
  childEvents: (parentId) => {
    const s = get();
    const compId = parentId != null ? parentId : s.events.currentEventId;

    return Array.from(s.events.items.values()).filter(
      (e) => e.parent != null && e.parent === compId,
    );
  },
  parentEvent: (parentId) => {
    const s = get();
    let compId: string | undefined = parentId;
    if (parentId == null) {
      const currentEvent = s.events.getter.currentEvent();
      compId = currentEvent?.parent || undefined;
    }

    if (compId == null) return null;
    const parentEvent = s.events.items.get(compId) || null;
    if (parentEvent == null)
      throw new Error(`Found no parent event for the setted parent id: ${compId}!`);
    if (!isGroupEvent(parentEvent))
      throw new Error(
        `The event ${compId} should be an group event, but has the type ${parentEvent.type}!`,
      );

    return parentEvent;
  },
  connectedBrands: (eventId) => {
    if (eventId == null) return [];
    const s = get();
    const event = s.events.items.get(eventId);
    if (!event) return [];

    return Array.from(s.brands.items.values()).filter((b) =>
      event.modules.brandedAppIds.includes(b.id),
    );
  },
  rrEventID: (eventId) => {
    let event: Immutable<RacemapEvent | CurrentEvent> | null = null;
    if (eventId == null) {
      event = get().events.getter.currentEvent();
    } else {
      event = get().events.items.get(eventId) || null;
    }

    if (event == null) return null;
    if (event.type === EventTypes.STAGE && event.parent != null) {
      // stages use the race result event id of the group
      const parentEvent = get().events.getter.parentEvent(event.parent);
      return parentEvent?.integrations.raceresult.eventId || null;
    }

    return event.integrations.raceresult.eventId;
  },
});
